import { LinearProgress } from '@mui/joy';

import { useNavigate, useParams } from 'react-router-dom';

import { Error } from '@/components/Hub/Error';
import { RepoForm, useRepoState } from '@/components/Hub/RepoForm';
import {
  useOrganizationId,
  useRepo,
  useUpdateRepoMutation,
} from '@/hooks/useSwr';
import { RepoSchema } from '@/types/schema';
import { addQueryParams } from '@/utils/add-query-params';
import { appHubIndexPath, appPromptsIndexPath } from '@/utils/constants';

const HubEditForm = ({
  owner,
  repoHandle,
  repoObj,
  inHub,
}: {
  owner: string;
  repoHandle: string;
  repoObj: RepoSchema;
  inHub: boolean;
}) => {
  const [repo, setRepo] = useRepoState({
    owner: owner,
    repo_handle: repoObj.repo_handle,
    description: repoObj.description ?? '',
    readme: repoObj.readme ?? '',
    is_public: repoObj.is_public,
    is_archived: repoObj.is_archived,
    tags: repoObj.tags,
  });

  const navigate = useNavigate();
  const organizationId = useOrganizationId();

  const { trigger: updateRepo, error: error } = useUpdateRepoMutation(
    owner,
    repoHandle!
  );

  const onSubmit = async () => {
    const res = await updateRepo({
      json: {
        description: repo.description,
        readme: repo.readme,
        is_public: repo.is_public,
        tags: repo.tags,
        is_archived: repo.is_archived,
      },
    });
    if (res !== undefined) {
      navigate(
        addQueryParams(
          `/${inHub ? appHubIndexPath : appPromptsIndexPath}/${
            inHub ? res.repo.full_name : res.repo.repo_handle
          }`,
          {
            organizationId,
          }
        )
      );
    }
  };

  return (
    <RepoForm
      repo={repo}
      setRepo={setRepo}
      tenantHandle={owner}
      onSubmit={onSubmit}
      error={error}
      mode="edit"
    />
  );
};

const HubEditRepo = ({ inHub }: { inHub: boolean }) => {
  const { owner, repo: repoHandle } = useParams();
  const { data: repoObj, isLoading, error } = useRepo(owner!, repoHandle!);

  if (error) {
    return <Error>{error.message}</Error>;
  }

  if (isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  if (!repoObj?.repo) {
    return <Error>An error occurred while loading this repo</Error>;
  }

  return (
    <HubEditForm
      owner={owner!}
      repoHandle={repoHandle!}
      repoObj={repoObj.repo!}
      inHub={inHub}
    />
  );
};

export default HubEditRepo;
